import { Component, ElementRef, ViewChild } from '@angular/core';
import { CanvasService } from '../canvas.service';
import { Session, SessionViewType, ShareAccessLevel } from '../canvas.model';
import { CanvasHelperService } from '../canvas-helper.service';

@Component({
  selector: 'app-canvas-sessions',
  templateUrl: './canvas-sessions.component.html',
  styleUrl: './canvas-sessions.component.scss',
})
export class CanvasSessionsComponent {
  readonly viewTypes = SessionViewType;
  readonly shareAccess = ShareAccessLevel;
  readonly userId = this.hubService.auth.getUserId();

  constructor(
    public hubService: CanvasService,
    public helperService: CanvasHelperService
  ) {}

  isSearchVisible: boolean = false;
  isBookmarkFilterEnabled: boolean = false;
  searchQuery: string = '';
  editingIndex: number | null = null;
  blurTimeout: any;

  @ViewChild('sessionTitleInput') editInputRef!: ElementRef;

  ngAfterViewChecked(): void {
    if (this.editingIndex !== null && this.editInputRef) {
      this.editInputRef.nativeElement.focus();
    }
  }

  startEdit(index: number): void {
    this.editingIndex = index;
  }

  saveEdit(session: any, inputRef: any): void {
    clearTimeout(this.blurTimeout);
    let updatedTitle = inputRef?.value?.trim();
    if (updatedTitle?.length > 0 && session.title?.trim() != updatedTitle) {
      session.isSaving = true;
      this.saveSessionTitle(session, inputRef.value);
    }
    this.cancelEdit();
  }

  onInputBlur(session: any, inputRef: any): void {
    this.blurTimeout = setTimeout(() => {
      this.saveEdit(session, inputRef);
    }, 100);
  }

  saveSessionTitle(session: any, value: any) {
    session.title = value;
    this.hubService.changeChatTitle(session._id, value).subscribe({
      next: (response: any) => {},
      error: (error: any) => {
        session.isSaving = false;
        this.hubService.showErrorMessage(
          'Unable to save session title. Please try again.'
        );
      },
      complete: () => {
        session.isSaving = false;
      },
    });
  }

  cancelEdit(): void {
    clearTimeout(this.blurTimeout);
    this.editingIndex = null;
  }

  toggleSearch() {
    this.isSearchVisible = !this.isSearchVisible;
    if (!this.isSearchVisible) {
      this.searchQuery = '';
    }
  }

  handleSearchBlur() {
    if (!this.searchQuery) {
      this.isSearchVisible = false;
    }
  }

  private filterSessions(isMySession: boolean): Session[] {
    if (!this.hubService.sessions) return [];
    const query = this.searchQuery.trim().toLowerCase();

    return this.hubService.sessions.filter((session: Session) => {
      const matchesSessionType = session.isMySession === isMySession;
      const matchesBookmark =
        !this.isBookmarkFilterEnabled || session.bookmarked;
      const matchesQuery =
        !query || session.title?.toLowerCase().includes(query);

      return matchesSessionType && matchesBookmark && matchesQuery;
    });
  }

  get filteredSessions(): Session[] {
    return this.filterSessions(true);
  }

  get filteredSharedSessions(): Session[] {
    return this.filterSessions(false);
  }

  checkSharedWith(shared_with: any = []): number {
    return (
      shared_with?.filter((user: any) => user.user_id !== this.userId).length ??
      0
    );
  }

  getSharedTooltip(session: any) {
    if (
      this.hubService.sessionViewType ==
        this.viewTypes.SESSIONS_SHARED_WITH_ME &&
      session.shared_with?.length === 1 &&
      session.shared_with[0]?.shared_by?.length > 0
    ) {
      return `Session shared by ${session.shared_with[0]?.shared_by}`;
    }
    return `Session shared with ${
      session.shared_with?.length > 1
        ? session.shared_with?.length + ' users'
        : session.shared_with[0]?.user_email
    }`;
  }

  switchViewType() {
    if (this.hubService.sessionViewType === this.viewTypes.MY_SESSIONS) {
      this.hubService.sessionViewType = this.viewTypes.SESSIONS_SHARED_WITH_ME;
    } else {
      this.hubService.sessionViewType = this.viewTypes.MY_SESSIONS;
    }
  }
}
